import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { DONATE } from "@/constants/testIds";
import { toast } from "sonner";

export default function DonateModal({ open, onClose, preset }) {
  const [form, setForm] = useState({ amount: "", tier_id: "", name: "", email: "", message: "", anonymous: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm((f) => ({ ...f, amount: preset?.amount || f.amount || 50, tier_id: preset?.tier_id || "" }));
    }
  }, [open, preset]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const amount = Number(form.amount);
    if (!amount || amount < 1) return toast.error("Enter at least $1");
    if (!form.name.trim()) return toast.error("Name is required");
    if (!form.email.trim()) return toast.error("Email is required");
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        amount,
        tier_id: form.tier_id || null,
        name: form.name,
        email: form.email,
        message: form.message || null,
        anonymous: form.anonymous,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not start checkout";
      toast.error(String(msg));
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid={DONATE.modal} className="rounded-none bg-slate-950 border border-white/15 max-w-md">
        <DialogHeader>
          <div className="font-mono text-[10px] uppercase tracking-widest text-kinetic">// Donate by card</div>
          <DialogTitle className="font-display text-2xl font-black uppercase tracking-tight">Back the fleet</DialogTitle>
          <DialogDescription className="text-slate-400">
            Stripe test mode — use card <span className="font-mono text-slate-300">4242 4242 4242 4242</span>, any future expiry, any CVC.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Amount (USD)</Label>
            <Input
              data-testid={DONATE.amount}
              type="number" min={1} step={1} required
              value={form.amount}
              onChange={(e) => set("amount")(e.target.value)}
              className="rounded-none bg-slate-900 border-white/15 h-11 font-mono text-lg"
            />
            <div className="mt-2 flex flex-wrap gap-2">
              {[25, 100, 500, 2500].map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => set("amount")(v)}
                  className="px-3 py-1 border border-white/15 hover:border-kinetic hover:text-kinetic font-mono text-xs"
                >
                  ${v}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Name</Label>
              <Input data-testid={DONATE.name} required value={form.name} onChange={(e) => set("name")(e.target.value)} className="rounded-none bg-slate-900 border-white/15 h-10" />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Email</Label>
              <Input data-testid={DONATE.email} required type="email" value={form.email} onChange={(e) => set("email")(e.target.value)} className="rounded-none bg-slate-900 border-white/15 h-10" />
            </div>
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Message (optional)</Label>
            <Textarea
              data-testid={DONATE.message}
              value={form.message}
              onChange={(e) => set("message")(e.target.value)}
              rows={2}
              placeholder="Why you're backing..."
              className="rounded-none bg-slate-900 border-white/15"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <Checkbox data-testid={DONATE.anonymous} checked={form.anonymous} onCheckedChange={set("anonymous")} />
            Show as anonymous on the wall
          </label>
          <div className="flex gap-2 pt-2">
            <Button data-testid={DONATE.close} type="button" variant="outline" onClick={onClose} className="rounded-none border-white/15 flex-1">
              Cancel
            </Button>
            <Button data-testid={DONATE.submit} disabled={busy} type="submit" className="rounded-none bg-kinetic hover:bg-[#FF5E1A] text-black font-bold uppercase flex-1">
              {busy ? "Redirecting…" : "Continue to Stripe"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
